import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {CampaignAnalyticsToolName} from '../campaignAnalyticsAgent.enums';
import analyticsApi from 'common/src/modules/analytics/analytics.api';
import {DateTime} from 'luxon';
import {
  DimensionValue,
  MetricsGroupBy,
  MetricValue,
} from 'common/src/modules/analytics/analytics.types';
import getTimezoneObjectByAbbreviation from 'common/src/modules/timezones/utils/getTimezoneObjectByAbbreviation';
import {isoDateUtcSchema} from 'common/src/modules/shared/copilot/copilot.schemas';
import {CampaignTreeType, FCCampaign} from 'common/src/modules/campaigns/campaigns.types';
import {OutputType} from 'common/src/modules/nodes/nodes.types';
import _find from 'lodash/find';
import {nodeTypeConfig} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import forceTimeZone from 'common/src/modules/timezones/utils/forceTimeZone';

const inputSchema = z.object({
  startDate: isoDateUtcSchema.describe('Requested start date (start of the day)'),
  endDate: isoDateUtcSchema.describe('Requested end date (end of the day)'),
  nodeId: z.number().describe('Requested ID of the card to analyze fallback for'),
});

export function getMetricFromUrl(url: string): string | null {
  const queryString = url.split('?')[1];
  if (!queryString) return null;

  const params = new URLSearchParams(queryString);

  return params.get('metric');
}

function nodeFallbackAnalyzerTool(store: CLStore) {
  async function execute({
    input: {startDate, endDate, nodeId},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const metric = getMetricFromUrl(window.location.href) as MetricValue;
    const state = store.getState();
    const {
      campaignPage: {insightsPage, campaignTree, campaignLogic},
    } = state;

    const campaignId = (campaignTree.campaign as FCCampaign).id;
    const measuredPeriod = insightsPage.measuredPeriod;

    const nodes = campaignLogic.nodes[CampaignTreeType.Campaign];
    const node = nodes[nodeId];

    if (!node) {
      return toolError(`Node with id ${nodeId} not found`);
    }

    const timezoneObject = await getTimezoneObjectByAbbreviation(measuredPeriod.timezone);
    const dateRange = {
      startDate: forceTimeZone(DateTime.fromISO(startDate).toUTC(), 0),
      endDate: forceTimeZone(DateTime.fromISO(endDate).toUTC(), 0).endOf('day'),
    };

    try {
      const data = await analyticsApi.getCampaignInsightsAnalytics({
        campaignId,
        sourceNodeId: nodeId,
        metrics: [metric],
        dimensions: [
          DimensionValue.CartOutputId,
          DimensionValue.HouseholdProfileStatus,
          DimensionValue.PersonProfileStatus,
        ],
        dateRange,
        topPerforming: true,
        limit: 100,
        selectedTimezone: timezoneObject,
        groupBy: MetricsGroupBy.Day,
        offset: 0,
      });

      return toolResult(
        JSON.stringify({
          fallbackOutputId: _find(node.outputs, {type: OutputType.Fallback})?.id,
          nodeName: node.name ?? `${nodeTypeConfig[node.type].name}`,
          nodeId: node.id,
          analysis: data,
          outputs: node.outputs,
          metric,
        }),
      );
    } catch (_error) {
      return toolError('Sorry data is unavailable, please try again later');
    }
  }

  return tool({
    name: CampaignAnalyticsToolName.AnalyzeNodeFallback,
    displayName: "Analyzing card's fallback",
    description: 'investigate and analyze node fallback by node id',
    execute,
    inputSchema,
  });
}

export default nodeFallbackAnalyzerTool;
